import React, { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { Sprite, Container } from "@pixi/react";
import { useButtonHover } from "../../../../Hooks/useButtonHover";
import { useDispatch, useSelector } from "react-redux";
import { SOUND_NAME } from "../../../../sounds";
import { getUITexture } from "../../../../assets";
import { useDimensions } from "../../../../Contexts/DimensionsContext";
import { soundManager } from "../../../../SoundManager";
import { decrementBet, closeMenu } from "../../../../State/actions";
import { FEATURES_CONFIG } from "../../../../featuresConfig";
import {
  selectIsSpinning,
  selectUserInterfaceDisabled,
  selectSessionExpired,
  selectBet,
  selectBets,
  selectAutoPlayActive,
  selectHoldAndWinActive,
  selectFreeRoundsActive,
  selectFreeSpinActive,
  selectRoyalCollectActive,
} from "../../../../State/selectors";
import { PixiSettings } from "../../../../pixiSettings";

const BET_CONTROL_BUTTON_CONFIG = {
  BASE_WIDTH: 45,
  BASE_HEIGHT: 45,

  DEVICE_CONFIGS: {
    [PixiSettings.DEVICE.MOBILE]: {
      [PixiSettings.ORIENTATION.PORTRAIT]: {
        deviceMultiplier: 1.4,
      },
      [PixiSettings.ORIENTATION.LANDSCAPE]: {
        deviceMultiplier: 1.2,
      },
    },
    [PixiSettings.DEVICE.TABLET]: {
      [PixiSettings.ORIENTATION.PORTRAIT]: {
        deviceMultiplier: 1.2,
      },
      [PixiSettings.ORIENTATION.LANDSCAPE]: {
        deviceMultiplier: 1.2,
      },
    },
    [PixiSettings.DEVICE.DESKTOP]: {
      deviceMultiplier: 1.0,
    },
  },
};

const ButtonDecrementBet = ({ x = 0, y = 0 }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { getResponsiveConfig } = useDimensions();

  const isSpinning = useSelector(selectIsSpinning);
  const bet = useSelector(selectBet);
  const bets = useSelector(selectBets);
  const sessionExpired = useSelector(selectSessionExpired);
  const autoPlayActive = useSelector(selectAutoPlayActive);
  const userInterfaceDisabled = useSelector(selectUserInterfaceDisabled);
  const holdAndWinActive = useSelector(selectHoldAndWinActive);
  const freeRoundsActive = useSelector(selectFreeRoundsActive);
  const freeSpinActive = useSelector(selectFreeSpinActive);
  const royalCollectActive = useSelector(selectRoyalCollectActive);

  // Scale hover + press effect
  const { hoverContainerRef, buttonRef, handlePointerOver, handlePointerOut, triggerPress } = useButtonHover();

  const texture = getUITexture("icon_minus");

  // Memoize calculations
  const currentBetIndex = useMemo(() => bets.indexOf(bet), [bets, bet]);

  const canDecreaseBet = useMemo(
    () =>
      currentBetIndex > 0 &&
      !isSpinning &&
      !sessionExpired &&
      !autoPlayActive &&
      !userInterfaceDisabled &&
      !holdAndWinActive &&
      !freeSpinActive &&
      !royalCollectActive &&
      !(FEATURES_CONFIG.FREE_ROUNDS.ENABLED && freeRoundsActive),
    [
      currentBetIndex,
      isSpinning,
      sessionExpired,
      autoPlayActive,
      userInterfaceDisabled,
      holdAndWinActive,
      freeSpinActive,
      royalCollectActive,
      freeRoundsActive,
    ]
  );

  // Get responsive configuration from context - memoized
  const buttonConfig = useMemo(
    () => getResponsiveConfig(BET_CONTROL_BUTTON_CONFIG, true),
    [getResponsiveConfig]
  );

  const handleClick = useCallback(() => {
    dispatch(closeMenu());

    if (canDecreaseBet) {
      soundManager.play(SOUND_NAME.button_click);
      triggerPress();
      dispatch(decrementBet());
    }
  }, [canDecreaseBet, dispatch, triggerPress]);

  return (
    <Container x={x} y={y}>
      <Container ref={hoverContainerRef}>
        <Sprite
          ref={buttonRef}
          texture={texture}
          name="secondary_button_decrement_bet"
          anchor={{ x: 0, y: 0.5 }}
          width={buttonConfig.width}
          height={buttonConfig.height}
          x={0}
          y={0}
          eventMode={canDecreaseBet ? "static" : "none"}
          cursor={canDecreaseBet ? "pointer" : "default"}
          alpha={canDecreaseBet ? 1 : 0.5}
          onpointertap={handleClick}
          onpointerover={handlePointerOver}
          onpointerout={handlePointerOut}
          accessible={true}
          accessibleTitle={t("bet_decrement", { ns: "common" })}
        />
      </Container>
    </Container>
  );
};

export default ButtonDecrementBet;
